import { AuthError, Session, SupabaseClient } from '@supabase/supabase-js';
import {
	createContext,
	PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';

// Define todos os estados possíveis da sessão para segurança de tipos
export type SessionContext =
	| {
		isLoading: true;
		session: null;
		error: null;
		supabaseClient: SupabaseClient;
	}
	| {
		isLoading: false;
		session: Session;
		error: null;
		supabaseClient: SupabaseClient;
	}
	| {
		isLoading: false;
		session: null;
		error: AuthError;
		supabaseClient: SupabaseClient;
	}
	| {
		isLoading: false;
		session: null;
		error: null;
		supabaseClient: SupabaseClient;
		isRecoveringPassword: false;
	};

const SessionContext = createContext<SessionContext & { isRecoveringPassword: boolean; setIsRecoveringPassword: (v: boolean) => void }>({
	isLoading: true,
	session: null,
	error: null,
	supabaseClient: {} as SupabaseClient,
	isRecoveringPassword: false,
	setIsRecoveringPassword: () => {}
});

export interface SessionContextProviderProps {
	supabaseClient: SupabaseClient;
}

export const SessionContextProvider = ({
	supabaseClient,
	children
}: PropsWithChildren<SessionContextProviderProps>) => {
	const [session, setSession] = useState<Session | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<AuthError>();
	const [isRecoveringPassword, setIsRecoveringPassword] = useState<boolean>(false);

	useEffect(() => {
		let mounted = true;

		async function getSession() {
			const {
				data: { session },
				error
			} = await supabaseClient.auth.getSession();

			// Apenas atualiza o estado se o componente ainda estiver montado
			if (mounted) {
				if (error) {
					setError(error);
					setIsLoading(false);
					return;
				}

				setSession(session);
				setIsLoading(false);
			}
		}

		getSession();

		return () => {
			mounted = false;
		};
	}, [supabaseClient]);

	useEffect(() => {
		const {
			data: { subscription }
		} = supabaseClient.auth.onAuthStateChange((event, session) => {
			if (
				session &&
				(event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'PASSWORD_RECOVERY')
			) {
				setSession(session);
				setError(undefined);
				if (event === 'PASSWORD_RECOVERY') {
					setIsRecoveringPassword(true);
				}
			}

			if (event === 'SIGNED_OUT') {
				setSession(null);
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [supabaseClient]);

	const value = useMemo(() => {
		if (isLoading) {
			return {
				isLoading: true,
				session: null,
				error: null,
				supabaseClient,
				isRecoveringPassword,
				setIsRecoveringPassword
			} as any;
		}

		if (error) {
			return {
				isLoading: false,
				session: null,
				error,
				supabaseClient,
				isRecoveringPassword,
				setIsRecoveringPassword
			} as any;
		}

		return {
			isLoading: false,
			session,
			error: null,
			supabaseClient,
			isRecoveringPassword,
			setIsRecoveringPassword
		} as any;
	}, [isLoading, session, error, supabaseClient, isRecoveringPassword]);

	return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

// Hooks para consumir o contexto
export const useSessionContext = () => {
	const context = useContext(SessionContext);
	if (context === undefined) {
		throw new Error(`useSessionContext must be used within a SessionContextProvider.`);
	}
	return context;
};

// CORREÇÃO: O hook useSession deve retornar apenas a sessão ou nulo.
export const useSession = () => {
	const context = useSessionContext();
	return context.session;
};

export const useUser = () => {
	const context = useSessionContext();
	return context.session?.user ?? null;
}

export const useSupabaseClient = () => {
	const context = useSessionContext();
	return context.supabaseClient;
};

export const useIsRecoveringPassword = () => {
	const context = useSessionContext();
	return {
		isRecoveringPassword: context.isRecoveringPassword,
		setIsRecoveringPassword: context.setIsRecoveringPassword
	};
};